import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { SideMenu } from './SideMenu';
import { useAuth } from '../../contexts/AuthContext';
import { Routes as RouterRoutes, Route, useLocation } from 'react-router-dom';
import { HomePage } from '../pages/HomePage';
import { About } from '../pages/About';
import { FAQ as FAQPage } from '../pages/FAQ';
import { Contact } from '../pages/Contact';
import { Pricing } from '../pages/Pricing';
import { TermsOfPolicy } from '../pages/TermsOfPolicy';
import { PrivacyPolicy } from '../pages/PrivacyPolicy';
import { GetOffers } from '../pages/GetOffers';
import GetOffersFunnel from '../../pages/GetOffersFunnel';
import GetOffersPricing from '../../pages/GetOffersPricing';
import { Dashboard } from '../pages/Dashboard';
import { Generator } from '../pages/Generator';
import StartFunnel from '../../pages/StartFunnel';
import { NotFound } from '../pages/NotFound';

interface MainLayoutProps {
  children?: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1 flex">
        {user && isDashboard && (
          <>
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden fixed bottom-4 right-4 z-50 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary-hover transition-colors"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
              <div 
                className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-hidden="true"
              />
            )}

            {/* Sidebar - Desktop & Mobile */}
            <div 
              className={`
                fixed lg:static top-0 left-0 h-full w-64 bg-white border-r z-40 
                transform transition-transform duration-300 ease-in-out
                lg:transform-none lg:z-0
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
              `}
            >
              <div className="h-full pt-[72px] overflow-y-auto">
                <SideMenu onClose={() => setIsMobileMenuOpen(false)} />
              </div>
            </div>
          </>
        )}
        
        <main 
          className={`
            flex-1 w-full transition-all duration-300 ease-in-out
            pt-[72px] px-4 md:px-6 lg:px-8
          `}
        >
          <div className="max-w-7xl mx-auto py-0">
            <RouterRoutes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<TermsOfPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/getoffers" element={<GetOffers />} />
              <Route path="/getoffers/funnel" element={<GetOffersFunnel />} />
              <Route path="/getoffers/pricing" element={<GetOffersPricing />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/history" element={<Dashboard />} />
              <Route path="/dashboard/documents" element={<Dashboard />} />
              <Route path="/dashboard/profile" element={<Dashboard />} />
              <Route path="/generator" element={<Generator />} />
              <Route path="/start" element={<StartFunnel />} />
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </div>
        </main>
      </div>
      
      <Footer className="mt-auto" />
    </div>
  );
}